import React from 'react';
import { SxProps, styled, useThemeProps } from '@mui/material/styles';
import { Theme, capitalize } from '@mui/material';
import clsx from 'clsx';
import { OverridableComponent, OverridableTypeMap, OverrideProps } from '@mui/material/OverridableComponent';
import composeClasses from '@mui/utils/composeClasses';
import { COMPONENT_NAME, getTileUtilityClass } from './tileClasses';

export type TilePropsVariantOverrides = object;

export interface TileOwnProps {
    /**
     * The content of the component.
     */
    children?: React.ReactNode | React.ReactNode[];
    /**
     * @default 'default'
     */
    color?: 'default' | 'primary' | 'error' | 'info' | 'success';

    /**
     * The system prop that allows defining system overrides as well as additional CSS styles.
     */
    sx?: SxProps<Theme>;
    /**
     * The variant to use.
     * @default 'elevation'
     */
}
export interface TileTypeMap<AdditionalProps = object, RootComponent extends React.ElementType = 'div'> {
    props: AdditionalProps & TileOwnProps;
    defaultComponent: RootComponent;
}

export interface ExtendTileTypeMap<TypeMap extends OverridableTypeMap, Keys extends string = ''> {
    props: TypeMap['props'] & Omit<TileTypeMap['props'], Keys>;
    defaultComponent: TypeMap['defaultComponent'];
}

export type TileProps<
    RootComponent extends React.ElementType = TileTypeMap['defaultComponent'],
    AdditionalProps = object,
> = OverrideProps<TileTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
    component?: React.ElementType;
};

const TileThemed = styled('div', {
    name: COMPONENT_NAME,
    slot: 'Root',
    overridesResolver: (props, styles) => {
        const { ownerState } = props;
        return [styles.root, styles[ownerState.variant], styles[`color${capitalize(ownerState.color)}`]];
    },
})<TileOwnProps & { ownerState: Partial<TileOwnProps> }>(() => ({}));

const useUtilityClasses = (ownerState: Omit<TileProps, 'color'> & { color: string }) => {
    const { color, classes = {} } = ownerState;

    const slots = {
        root: ['root', `color${capitalize(color)}`],
    };

    return composeClasses(slots, getTileUtilityClass, classes);
};

export const Tile = React.forwardRef<HTMLDivElement, TileProps>(function (inProps, ref) {
    const props = useThemeProps({ props: inProps, name: COMPONENT_NAME });
    const { color: colorProp = 'default', component, ...other } = props;

    const ownerState = {
        ...props,
        color: colorProp,
    };

    const classes = useUtilityClasses(ownerState);

    return (
        <TileThemed
            as={component}
            ref={ref}
            ownerState={ownerState}
            className={clsx(classes.root, props?.className)}
            {...other}
        />
    );
}) as OverridableComponent<TileTypeMap>;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Maybe we should consider removing the displayName rule
//@ts-ignore
Tile.displayName = 'Tile';

export { tileClasses, type TileClasses, type TileClassKey } from './tileClasses';
